
// src/pages/Clinic/StaffProfilePage.jsx
import { useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, Divider, Avatar, Chip, CircularProgress, alpha, useTheme, useMediaQuery, MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axiosInstance from '../../axiosInstance';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';

const paperStyle = (theme) => ({
  p: { xs: 2, sm: 3 },
  borderRadius: '16px',
  boxShadow: 'none',
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderColor: alpha(theme.palette.primary.main, 0.2),
  }
});

export default function StaffProfilePage() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, refreshAuthData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clinicLoading, setClinicLoading] = useState(true);

  const [membership, setMembership] = useState(null); // { id, rol, perfil }
  const [clinicId, setClinicId] = useState(null);

  // Datos de Usuario (base): first_name, last_name, email, phone_number
  const [uform, setUform] = useState({ first_name: '', last_name: '', email: '', phone_number: '' });

  // Perfil (ClinicaMembership.perfil) — JSON libre con llaves comunes
  const [pform, setPform] = useState({
    numero_empleado: '',
    departamento: '',
    cargo: '',
    extension: '',
    turno: '',
    supervisor: '',
    emergencia_nombre: '',
    emergencia_telefono: '',
    idiomas: '',
    certificaciones: '',
    // Enfermería:
    colegiacion: '',
    colegiacion_vigente: false,
    areas_cobertura: '',
    // Recepción/Admin:
    ventanilla: '',
    permisos_caja: false,
    maneja_agenda: true,
  });

  const role = membership?.rol || user?.rol || 'Recepción'; // default

  useEffect(() => {
    if (!user) return;
    setUform({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
    });
  }, [user]);

  useEffect(() => {
    // Obtener clínica activa del usuario
    (async () => {
      try {
        const { data } = await axiosInstance.get('/clinics/my-memberships/');
        const first = (data || []).find(m => m.activo);
        if (first?.clinica?.id) {
          setClinicId(first.clinica.id);
          // Cargar mi perfil dentro de esa clínica
          const { data: me } = await axiosInstance.get(`/clinicas/${first.clinica.id}/my-profile/`);
          setMembership(me);
          const pf = me?.perfil || {};
          setPform(prev => ({ ...prev, ...pf }));
        } else {
          toast.info('No perteneces a ninguna clínica activa. Algunas funciones pueden no estar disponibles.');
        }
      } catch (e) {
        console.warn('No se pudo cargar membresía clínica', e?.response?.status);
      } finally {
        setClinicLoading(false);
        setLoading(false);
      }
    })();
  }, []);

  const onUChange = (e) => {
    const { name, value } = e.target;
    setUform(p => ({ ...p, [name]: value }));
  };
  const onPChange = (e) => {
    const { name, value } = e.target;
    setPform(p => ({ ...p, [name]: value }));
  };

  const onPToggle = (name) => (_, checked) => setPform(p => ({ ...p, [name]: checked }));

  const onSave = async () => {
    setSaving(true);
    try {
      // 1) Guardar datos base del usuario
      await axiosInstance.patch('/user/', {
        first_name: uform.first_name,
        last_name: uform.last_name,
        email: uform.email,
        phone_number: uform.phone_number,
      });

      // 2) Guardar perfil (si hay clínica activa)
      if (clinicId) {
        await axiosInstance.put(`/clinicas/${clinicId}/my-profile/`, {
          perfil: pform
        });
      }
      toast.success('Perfil actualizado');
      await refreshAuthData?.();
    } catch (e) {
      const data = e?.response?.data || {};
      const errMsg = data?.email?.[0] || data?.detail || data?.error || 'No se pudo guardar el perfil';
      toast.error(errMsg);
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }} sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant={isXs ? 'h5' : 'h4'} sx={{ fontWeight: 'bold' }}>
          Mi Perfil ({role})
        </Typography>
        <Chip label={role} color="primary" variant="outlined" />
      </Box>

      <Grid container spacing={3}>
        {/* Datos personales (Usuario) */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={paperStyle(theme)}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ mb: 0 }}>Datos personales</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Nombres" name="first_name" value={uform.first_name} onChange={onUChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Apellidos" name="last_name" value={uform.last_name} onChange={onUChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Correo" name="email" value={uform.email} onChange={onUChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Teléfono" name="phone_number" value={uform.phone_number} onChange={onUChange} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Datos del puesto */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={paperStyle(theme)}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                <WorkIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ mb: 0 }}>Datos del puesto</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Número de empleado" name="numero_empleado" value={pform.numero_empleado} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Departamento" placeholder="Admisión, Enfermería, Administración..." name="departamento" value={pform.departamento} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Cargo / Puesto" name="cargo" value={pform.cargo} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="turno-label">Turno</InputLabel>
                  <Select labelId="turno-label" label="Turno" value={pform.turno || ''} onChange={(e) => setPform(p => ({ ...p, turno: e.target.value }))}>
                    <MenuItem value=""><em>No especificado</em></MenuItem>
                    <MenuItem value="Matutino">Matutino</MenuItem>
                    <MenuItem value="Vespertino">Vespertino</MenuItem>
                    <MenuItem value="Nocturno">Nocturno</MenuItem>
                    <MenuItem value="Mixto">Mixto</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Extensión telefónica" name="extension" value={pform.extension} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Supervisor" name="supervisor" value={pform.supervisor} onChange={onPChange} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Contactos e idiomas */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={paperStyle(theme)}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                <LocalPhoneIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" sx={{ mb: 0 }}>Idiomas y contacto de emergencia</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Idiomas" placeholder="Español, Inglés" name="idiomas" value={pform.idiomas} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Certificaciones (coma separadas)" placeholder="BLS, ACLS" name="certificaciones" value={pform.certificaciones} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Contacto de emergencia - Nombre" name="emergencia_nombre" value={pform.emergencia_nombre} onChange={onPChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Contacto de emergencia - Teléfono" name="emergencia_telefono" value={pform.emergencia_telefono} onChange={onPChange} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Sección condicional por rol */}
        {role === 'Enfermería' && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={paperStyle(theme)}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                  <BadgeIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 0 }}>Credenciales de Enfermería</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" fullWidth label="N° de colegiación de Enfermería" name="colegiacion" value={pform.colegiacion} onChange={onPChange} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel control={<Switch checked={!!pform.colegiacion_vigente} onChange={onPToggle('colegiacion_vigente')} />} label="Colegiación vigente" />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Áreas de cobertura" placeholder="Urgencias, Hospitalización..." name="areas_cobertura" value={pform.areas_cobertura} onChange={onPChange} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {(role === 'Recepción' || role === 'Administrador') && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={paperStyle(theme)}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                  <SecurityIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 0 }}>Operación / Caja</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" fullWidth label="Ventanilla / Caja" name="ventanilla" value={pform.ventanilla} onChange={onPChange} />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel control={<Switch checked={!!pform.permisos_caja} onChange={onPToggle('permisos_caja')} />} label="Permisos de caja" />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel control={<Switch checked={!!pform.maneja_agenda} onChange={onPToggle('maneja_agenda')} />} label="Gestiona agenda" />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Acciones */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={onSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
              Guardar cambios
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>Deshacer</Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
